import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { MdArrowForward, MdDescription } from 'react-icons/md';
import { surveyApi } from '../../../api/surveyApi';
import './Survey.css';

const normalizeSurveySummary = (item = {}) => ({
  surveyId: String(item.surveyId || item.id || item.uuid || ''),
  title: item.title || item.surveyTitle || 'Untitled Survey',
});

const normalizeSurveyDetails = (payload = {}) => ({
  surveyId: String(payload.surveyId || payload.id || payload.uuid || ''),
  title: payload.title || payload.surveyTitle || 'Survey',
  questions: Array.isArray(payload.questions)
    ? payload.questions.map((question) => ({
      questionId: String(question.questionId || question.id || question.uuid || ''),
      question: question.question || question.text || '',
      type: question.type || 'TEXT',
      required: Boolean(question.required),
      options: Array.isArray(question.options)
        ? question.options.map((option) => ({
          optionId: String(option.optionId || option.id || option.uuid || option.value || ''),
          optionText: option.optionText || option.label || option.value || '',
        }))
        : [],
    }))
    : [],
});

const normalizeSurveyList = (payload) => {
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.surveys)
      ? payload.surveys
      : payload
        ? [payload]
        : [];

  return list
    .map(normalizeSurveySummary)
    .filter((item) => item.surveyId);
};

function SurveyPublished() {
  const [surveyList, setSurveyList] = useState([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState('');
  const [surveyData, setSurveyData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState({ loadingSurveyList: false, loadingSurvey: false, submitting: false });

  const setBusyState = (key, value) => {
    setBusy((prev) => ({ ...prev, [key]: value }));
  };

  const handleLoadAvailableSurveys = async () => {
    setBusyState('loadingSurveyList', true);
    try {
      const payload = await surveyApi.getAvailableSurveys();
      const normalizedList = normalizeSurveyList(payload);
      setSurveyList(normalizedList);

      if (!normalizedList.length) {
        setSelectedSurveyId('');
        setSurveyData(null);
        toast.info('No surveys available for your role.');
        return;
      }

      if (!selectedSurveyId || !normalizedList.some((item) => item.surveyId === selectedSurveyId)) {
        setSelectedSurveyId(normalizedList[0].surveyId);
      }

      toast.success('Survey list loaded.');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || error.message || 'Failed to fetch available surveys.');
    } finally {
      setBusyState('loadingSurveyList', false);
    }
  };

  useEffect(() => {
    handleLoadAvailableSurveys();
  }, []);

  const handleLoadSelectedSurvey = async () => {
    if (!selectedSurveyId) {
      toast.warning('Please select a survey first.');
      return;
    }

    setBusyState('loadingSurvey', true);
    try {
      const payload = await surveyApi.getSurveyById(selectedSurveyId);
      const normalized = normalizeSurveyDetails(payload);
      if (!normalized.surveyId) {
        throw new Error('Survey details did not include surveyId.');
      }
      setSurveyData(normalized);
      setAnswers({});
      toast.success('Survey loaded.');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || error.message || 'Failed to fetch survey details.');
    } finally {
      setBusyState('loadingSurvey', false);
    }
  };

  const updateAnswer = (question, value) => {
    const key = String(question.questionId);
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const validateResponses = () => {
    if (!surveyData?.questions?.length) return true;
    for (const question of surveyData.questions) {
      if (!question.required) continue;
      const value = answers[String(question.questionId)];
      if (!String(value || '').trim()) return false;
    }
    return true;
  };

  const handleSubmitResponse = async (e) => {
    e.preventDefault();
    if (!surveyData?.surveyId) {
      toast.warning('Please load a survey first.');
      return;
    }
    if (!validateResponses()) {
      toast.warning('Please complete all required questions.');
      return;
    }

    setBusyState('submitting', true);
    try {
      const answersPayload = (surveyData.questions || []).map((question) => {
        const value = answers[String(question.questionId)] ?? '';
        if (Array.isArray(value)) {
          return {
            questionId: question.questionId,
            value,
          };
        }
        return {
          questionId: question.questionId,
          value: String(value),
        };
      });

      await surveyApi.submitSurvey({
        surveyId: surveyData.surveyId,
        answers: answersPayload,
      });

      toast.success('Survey submitted successfully.');
      await handleLoadAvailableSurveys();
      setSelectedSurveyId('');
      setSurveyData(null);
      setAnswers({});
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || error.message || 'Failed to submit survey.');
    } finally {
      setBusyState('submitting', false);
    }
  };

  const renderQuestionInput = (question) => {
    const key = String(question.questionId);
    const value = answers[key] ?? '';

    if (question.type === 'SINGLE_CHOICE') {
      return (
        <div className="survey-options-grid">
          {(question.options || []).map((option) => (
            <label key={option.optionId || option.optionText} className="survey-option-item">
              <input
                type="radio"
                name={`survey-q-${key}`}
                value={option.optionId}
                checked={value === option.optionId}
                onChange={(e) => updateAnswer(question, e.target.value)}
              />
              <span>{option.optionText}</span>
            </label>
          ))}
        </div>
      );
    }

    if (question.type === 'MULTI_CHOICE' || question.type === 'MULTIPLE_CHOICE') {
      const multiValue = Array.isArray(value) ? value : [];
      return (
        <div className="survey-options-grid">
          {(question.options || []).map((option) => {
            const checked = multiValue.includes(option.optionId);
            return (
              <label key={option.optionId || option.optionText} className="survey-option-item">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    const next = checked
                      ? multiValue.filter((item) => item !== option.optionId)
                      : [...multiValue, option.optionId];
                    updateAnswer(question, next);
                  }}
                />
                <span>{option.optionText}</span>
              </label>
            );
          })}
        </div>
      );
    }

    if (question.type === 'DROPDOWN') {
      return (
        <select value={String(value)} onChange={(e) => updateAnswer(question, e.target.value)}>
          <option value="">Select an option</option>
          {(question.options || []).map((option) => (
            <option key={option.optionId || option.optionText} value={option.optionId}>{option.optionText}</option>
          ))}
        </select>
      );
    }

    if (question.type === 'RATING') {
      return (
        <select value={String(value)} onChange={(e) => updateAnswer(question, e.target.value)}>
          <option value="">Select rating</option>
          {[1, 2, 3, 4, 5].map((rating) => (
            <option key={rating} value={String(rating)}>{rating}</option>
          ))}
        </select>
      );
    }

    return (
      <input
        type="text"
        value={String(value)}
        onChange={(e) => updateAnswer(question, e.target.value)}
        placeholder="Type your answer"
      />
    );
  };

  return (
    <div className="survey-create-page">
      <div className="survey-create-shell">
        <div className="survey-create-head">
          <h2>My Surveys</h2>
          <p>Please share your feedback.</p>
        </div>

        <form onSubmit={handleSubmitResponse} className="survey-create-form">
          <div className="survey-form-group">
            <div className="survey-question-header">
              <label>Survey</label>
              {busy.loadingSurveyList && <span className="survey-info-line">Loading surveys...</span>}
            </div>

            <div className="survey-row">
              <select
                value={selectedSurveyId}
                onChange={(e) => setSelectedSurveyId(e.target.value)}
              >
                <option value="">Select survey</option>
                {surveyList.map((survey) => (
                  <option key={survey.surveyId} value={survey.surveyId}>{survey.title}</option>
                ))}
              </select>
              <button className="btn-secondary btn-add-green" type="button" onClick={handleLoadSelectedSurvey} disabled={busy.loadingSurvey || !selectedSurveyId}>
                <MdDescription />
                <span>{busy.loadingSurvey ? 'Loading...' : 'Open Survey'}</span>
              </button>
            </div>
          </div>

          {surveyData && (
            <div className="survey-form-group">
              <h4>{surveyData.title}</h4>

              <div className="survey-question-list">
                {(surveyData.questions || []).map((question, idx) => (
                  <div className="survey-question-card" key={String(question.questionId || idx)}>
                    <div className="survey-question-top">
                      <strong>
                        {idx + 1}. {question.question}
                      </strong>
                      {question.required && <span className="required">Required</span>}
                    </div>
                    <div className="survey-form-group compact">
                      {renderQuestionInput(question)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="survey-submit-row">
                <button className="btn-primary" type="submit" disabled={busy.submitting}>
                  <MdArrowForward />
                  <span>{busy.submitting ? 'Submitting...' : 'Submit'}</span>
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default SurveyPublished;
